/**
 * /plugins — your plugins, as a table. A row opens its workspace.
 *
 * The table is the page, trim to trim, as the OS-Evals table is (FleetScores).
 * Verification and Updated are filled in after the list arrives: the list
 * carries `is_verified` only, and a plugin has no updated time of its own, so
 * its newest version stands for it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import api from '../../services/api';
import { Plug } from 'lucide-react';
import { Button } from '../../components/common';
import PortalPage from '../../components/PortalPage';
import { useGroup } from '../../contexts/GroupContext';
import { kindLabel } from './manifest';
import './PluginWorkspace.css';

const COLUMNS = [
  { key: 'name', label: 'Plugin' },
  { key: 'kind', label: 'Kind' },
  { key: 'tools', label: 'Tools' },
  { key: 'verification', label: 'Verification' },
  { key: 'distribution', label: 'Publish' },
  { key: 'version', label: 'Version' },
  { key: 'updated', label: 'Updated' },
];

const VERIFICATION = { verified: 'Verified', requested: 'Requested', not_requested: 'Not requested' };

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

export default function PluginsList() {
  const navigate = useNavigate();
  const { activeGroupId } = useGroup();
  const [rows, setRows] = useState(null);
  const [extra, setExtra] = useState({});
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ by: 'updated', dir: 'desc' });

  useEffect(() => {
    let live = true;
    setRows(null);
    api.getMyPlugins(activeGroupId || undefined)
      .then((list) => {
        if (!live) return;
        setRows(list);
        // What the list doesn't carry: the verification state of the
        // unverified, and when each last changed.
        for (const p of list) {
          const v = p.is_verified ? Promise.resolve({ verification: { state: 'verified' } }) : api.getPlugin(p.id);
          const h = typeof api.getVersions === 'function' ? api.getVersions(p.id) : Promise.resolve(null);
          Promise.allSettled([v, h]).then(([a, b]) => {
            if (!live) return;
            const versions = b.value?.versions || [];
            const newest = versions.reduce((m, x) => (!m || x.created_at > m ? x.created_at : m), null);
            setExtra((e) => ({
              ...e,
              [p.id]: {
                verification: a.value?.verification?.state || (p.is_verified ? 'verified' : null),
                updated: newest || p.verified_at || p.created_at,
              },
            }));
          });
        }
      })
      .catch((e) => { if (live) setError(e.message || 'Could not load your plugins.'); });
    return () => { live = false; };
  }, [activeGroupId]);

  const view = useMemo(() => (rows || []).map((p) => {
    const x = extra[p.id] || {};
    const verification = x.verification || (p.is_verified ? 'verified' : null);
    return {
      id: p.id,
      name: p.name,
      description: p.description,
      kind: kindLabel(p.execution_type),
      tools: (p.tools || []).length || 1,
      verification,
      distribution: (p.deployment_mode || 'incubator') === 'public' ? 'Public' : 'Incubator',
      version: p.spec_version || 1,
      updated: x.updated || p.created_at,
    };
  }), [rows, extra]);

  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...view].sort((a, b) => {
      const av = a[sort.by] ?? '';
      const bv = b[sort.by] ?? '';
      if (typeof av === 'number' && typeof bv === 'number') return dir * (av - bv);
      return dir * String(av).localeCompare(String(bv));
    });
  }, [view, sort]);

  const click = (key) => setSort((s) => ({
    by: key,
    dir: s.by === key ? (s.dir === 'asc' ? 'desc' : 'asc') : (key === 'name' ? 'asc' : 'desc'),
  }));

  const open = (id) => navigate(`/plugins/${id}`);

  return (
    <PortalPage
      width="full"
      className="pl-page"
      title="Plugins"
      lede="Tools that agents and people run on someone's data. Each tool declares what it reads and writes, and is handed only that."
      actions={<Button onClick={() => navigate('/plugins/new')}>New plugin</Button>}
    >
      {error ? <p className="pw__err pl__msg">{error}</p> : null}
      {!rows && !error ? <p className="pw__muted pl__msg">Loading…</p> : null}
      {rows && !rows.length ? (
        // Into first-run onboarding, already on Plugin.
        <div className="pl__msg pl__empty">
          <p className="pw__muted">No plugins yet.</p>
          <Button onClick={() => navigate('/welcome?kind=plugin')}>Start</Button>
        </div>
      ) : null}
      {rows && rows.length ? (
        <div className="pl__scroll">
          <table className="pl__table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.key} className={c.key === 'name' ? 'is-left' : ''}
                      aria-sort={sort.by === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}>
                    <button type="button" onClick={() => click(c.key)} className={sort.by === c.key ? 'is-active' : ''}>
                      {c.label}{sort.by === c.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id} tabIndex={0} onClick={() => open(r.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter') open(r.id); }}>
                  <td className="is-left" data-label="Plugin">
                    <span className="pl__who">
                      <span className="pl__face" aria-hidden="true"><Plug size={15} /></span>
                      <span className="pl__names">
                        <span className="pl__name">{r.name}</span>
                        {r.description ? <span className="pl__sub">{r.description}</span> : null}
                      </span>
                    </span>
                  </td>
                  <td data-label="Kind">{r.kind}</td>
                  <td data-label="Tools">{r.tools}</td>
                  <td data-label="Verification">
                    {r.verification ? (
                      <span className={`pl__state pl__state--${r.verification}`}>{VERIFICATION[r.verification]}</span>
                    ) : <span className="pw__muted">…</span>}
                  </td>
                  <td data-label="Publish">{r.distribution}</td>
                  <td data-label="Version">v{r.version}</td>
                  <td data-label="Updated">{day(r.updated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </PortalPage>
  );
}
